import { useState } from 'react';
import { Button, Dialog, Icon } from '@dayon/design-system';
import { stamp } from './format.ts';

export interface InviteLink {
  displayName: string;
  email: string;
  role: string;
  /** Relative, with the token in the FRAGMENT. Origin is added in the browser. */
  linkPath: string;
  expiresAt: string;
  supersededLinks: number;
  /**
   * THE OUTBOX'S OWN VERDICT for the message this request recorded.
   *
   * Not a sentence the server phrased and not a claim this dialog makes:
   * `status` is the value in `platform.mail_message.status` at the moment the
   * transaction committed.
   *
   *   PENDING    — the worker hands it to the provider within the minute.
   *   SUPPRESSED — recorded and NOT delivered. Either this environment's
   *                MAIL_OUTBOUND_MODE says nobody may receive mail, or this
   *                address stands on the suppression list.
   */
  delivery: { messageRef: string; status: string; to: string };
}

/**
 * The invite link, shown once — and what became of the message that carries it.
 *
 * THE LINK IS SHOWN EITHER WAY, and that is the point of the dialog now.
 * `@dayon/mailer` records the message in the same transaction as the token, so
 * there is always a message; whether it is DELIVERED is configuration —
 * development and staging run MAIL_OUTBOUND_MODE=SUPPRESS and deliver nothing.
 * A dialog that said "an email is on its way" would be wrong on exactly those
 * environments, and a dialog that hid the link because it believed its own
 * claim would strand the operator. So this reports the status it was given and
 * shows the credential regardless.
 *
 * The token lives in the URL's FRAGMENT (`#token=…`), which browsers do not
 * send to servers. So the link can be pasted into the address bar without the
 * credential landing in an access log, a proxy log or a Referer header on the
 * way to the console; the accept screen reads it from the fragment and posts it
 * in a body.
 */
export function InviteLinkDialog({ link, onClose }: { link: InviteLink | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const absolute = link ? `${window.location.origin}${link.linkPath}` : '';

  async function copy() {
    if (!absolute) return;
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
    } catch {
      /* Clipboard access is refused in some contexts and over plain http on
         some browsers. The link is rendered in full above regardless, so a
         refusal costs a selection rather than the link — and saying "copied"
         when nothing was copied is the one outcome worth avoiding. */
      setCopied(false);
    }
  }

  return (
    <Dialog
      open={link !== null}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title={link ? `Invite link — ${link.displayName}` : ''}
      wide
      actions={
        <>
          <Button onClick={() => void copy()}>
            <Icon name="copy" />
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setCopied(false);
              onClose();
            }}
          >
            Done
          </Button>
        </>
      }
    >
      {link && (
        <div className="stack">
          {link.delivery.status === 'PENDING' ? (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                <strong>Queued for {link.delivery.to} as {link.delivery.messageRef}.</strong> The
                platform sends it within the minute. Nothing here can confirm it ARRIVED, so the link
                is below as well — if {link.displayName} does not have it shortly, give it to them
                over a channel you both already trust. Whoever holds it can set the password on
                this account.
              </div>
            </div>
          ) : (
            <div className="notice notice-quiet">
              <Icon name="warning-circle" />
              <div>
                {/* SUPPRESSED covers MAIL_OUTBOUND_MODE=SUPPRESS, an address outside
                    MAIL_ALLOWLIST, and the suppression list; "mail is switched off" was false for
                    the allow-list case (credential-outcome-words.test.ts). */}
                <strong>Recorded as {link.delivery.messageRef}, and NOT sent.</strong> This
                installation does not send mail to {link.delivery.to}, or the address is on the
                do-not-mail list — either way the message is marked as held back and nobody will
                receive it. Give
                this link to {link.displayName} yourself, in person or over a channel you both
                already trust. Whoever holds it can set the password on this account.
              </div>
            </div>
          )}

          <code className="secret-block">{absolute}</code>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* Only a SHA-256 digest of the token is stored. */}
              <strong>Single use, and it expires {stamp(link.expiresAt)}.</strong> The platform
              keeps only a one-way fingerprint of the link, so it cannot be read back out — this is
              the one time it is shown. Accepting it sets a password AND enrols an authenticator in
              one step, so {link.displayName} has a second factor before their first sign-in.
              {link.supersededLinks > 0
                ? ` Generating this revoked ${link.supersededLinks} earlier link${link.supersededLinks === 1 ? '' : 's'}, which no longer work.`
                : ''}
            </div>
          </div>

          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            The token sits after the <code>#</code>, which browsers never send to a server — so
            pasting this into an address bar does not put the credential into an access log.
            Generating another link for {link.displayName} revokes this one.
          </div>
        </div>
      )}
    </Dialog>
  );
}
